import uniq from 'lodash-es/uniq';
import { Directive, ViewChild, ElementRef, inject, OnDestroy, AfterViewInit } from '@angular/core';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { AbstractStripeComponent } from '@df/module-stripe/abstract-stripe.component';
import { Status } from '@df/core/status';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { TimeoutService } from '@df/ng/timeout.service';
import { StripeService } from '@df/module-stripe/stripe.service';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { BasketTotalsUpdatingService } from '@df/basket/view/basket-totals-updating.service';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class StripePaymentRequestButtonComponentDef
    extends AbstractStripeComponent<Stripe.IPaymentRequestButtonPaymentMethod, Stripe.IPaymentRequestButtonPaymentMethodConfig>
    implements OnDestroy, AfterViewInit
{
    /**
     * @type {Status}
     */
    createPaymentRequestButtonStatus = new Status();

    /**
     * @type {Status}
     */
    totalsUpdatingClickStatus = new Status();

    /**
     * GP on/off
     * @type {boolean}
     */
    protected useGooglePay = true;

    /**
     * AP on/off
     * @type {boolean}
     */
    protected useApplePay = true;

    /**
     * Browser cards on/off
     * @type {boolean}
     */
    protected useBrowserCard = false;

    /**
     * Link on/off
     * @type {boolean}
     */
    protected useLink = true;

    /**
     * Authentication status
     * @type {Status}
     */
    private authenticationStatus = new Status();

    /**
     * Payment Request Button Element
     * @type {Stripe.IPaymentRequestButtonElementInstance}
     */
    private paymentRequestButtonElementInstance?: Stripe.IPaymentRequestButtonElementInstance;

    /**
     * Data that affects payment request, on checkout data change we check if this data change -> if it is changed we "recreate"
     * element
     * @type {string}
     */
    private lastUpdateRequiredData?: string;

    /**
     * Method code
     * @type {Stripe.PaymentMethod}
     */
    protected readonly methodCode = Stripe.PaymentMethod.StripePaymentsPrapi;

    @ViewChild('container')
    private container?: ElementRef<any>;

    private basketTotalsUpdatingService = inject(BasketTotalsUpdatingService);
    private timeoutService = inject(TimeoutService);
    private onCheckoutDataUpdateDebounced = this.timeoutService.debounce(() => this.onCheckoutDataUpdate(), 1000);

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();

        [this.createPaymentRequestButtonStatus, this.totalsUpdatingClickStatus].forEach(i => {
            this.subscriptions.add(i.subscribe(() => this.detectChanges()));
        });

        this.subscriptions.add(
            this.basketTotalsUpdatingService.subject.subscribe(() => {
                if (!this.totalsUpdating) {
                    this.totalsUpdatingClickStatus.reset();
                }
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.onAvailabilityUpdate(false);
        this.clearPaymentRequestButtonElementInstance();
    }

    /**
     * Checks if elements can be displayed
     * @returns {boolean}
     */
    protected canDisplayElements(): boolean {
        return !!this.container?.nativeElement;
    }

    /**
     * On stripe correctly created callback
     */
    protected onStripeCreated(): void {}

    /**
     * Returns if component is ready
     * @returns {boolean}
     */
    protected isComponentReady(): boolean {
        return !!this.paymentRequestButtonElementInstance;
    }

    /**
     * On elements object created callback
     */
    protected onElementsCreated(): void {
        this.createPaymentRequestButton();
        this.checkoutStepComponent.forms.forEach(form => {
            if (form && form.valueChanges) {
                this.subscriptions.add(form.valueChanges.subscribe(() => this.onCheckoutDataUpdateDebounced()));
            }
        });
        this.subscriptions.add(this.app.customer.basket.subscribe(() => this.onCheckoutDataUpdateDebounced()));
    }

    /**
     * Sets payment request button component availability flag
     * @param {boolean} to
     */
    private onAvailabilityUpdate(to: boolean): void {
        this.stripeService.isPaymentRequestButtonAvailable = to;
    }

    //////////////////////////////////////////
    /////
    /////   Checkout data update
    /////
    //////////////////////////////////////////

    /**
     * Create payment request button
     */
    private createPaymentRequestButton(): void {
        this.app.request.send(
            this.createPaymentRequestButtonStatus,
            () => {
                return new Promise((resolve, reject) => {
                    const stripe = this.stripe;
                    const elements = this.elements;
                    const container = this.container?.nativeElement;
                    if (stripe && elements && container) {
                        this.lastUpdateRequiredData = JSON.stringify(this.getUpdateRequiredData());
                        this.stripeService.getPaymentRequestOptions(
                            undefined,
                            { type: 'checkout' },
                            (paymentRequestOptions: Stripe.IPaymentRequestOptions) => {
                                const options = paymentRequestOptions && this.getPaymentRequestOptions(paymentRequestOptions);
                                if (options) {
                                    const paymentRequestInstance = stripe.paymentRequest(options);
                                    paymentRequestInstance
                                        .canMakePayment()
                                        .then((result: Stripe.IPaymentRequestInstanceCanMakePaymentResult) => {
                                            if (result) {
                                                resolve({ data: paymentRequestInstance });
                                            } else {
                                                reject();
                                            }
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                } else {
                                    reject();
                                }
                            },
                            () => {
                                reject();
                            }
                        );
                    } else {
                        reject();
                    }
                });
            },
            (paymentRequestInstance: Stripe.IPaymentRequestInstance | undefined) => {
                this.onPaymentRequestInstanceUpdate(paymentRequestInstance);
            },
            () => {
                this.onPaymentRequestInstanceUpdate();
            }
        );
    }

    /**
     * Clears/remove existing payment request button
     */
    private clearPaymentRequestButtonElementInstance(): void {
        if (this.paymentRequestButtonElementInstance) {
            try {
                this.paymentRequestButtonElementInstance.clear();
                this.paymentRequestButtonElementInstance.destroy();
                this.paymentRequestButtonElementInstance.unmount();
                this.paymentRequestButtonElementInstance = undefined;
            } catch (e) {
                this.paymentRequestButtonElementInstance = undefined;
            }
        }
        this.readyCheck();
    }

    /**
     * On payment request instance callback
     * @param {Stripe.IPaymentRequestInstance} paymentRequestInstance
     */
    private onPaymentRequestInstanceUpdate(paymentRequestInstance?: Stripe.IPaymentRequestInstance): void {
        if (!this.totalsUpdating) {
            this.totalsUpdatingClickStatus.reset();
        }

        this.clearPaymentRequestButtonElementInstance();
        if (paymentRequestInstance) {
            if (!this.elements) {
                throw new Error('Elements not created');
            }
            const paymentRequestButton = this.elements.create(
                Stripe.Component.PaymentRequestButton,
                this.getPaymentRequestButtonCreateOptions(paymentRequestInstance)
            );
            this.paymentRequestButtonElementInstance = paymentRequestButton;
            paymentRequestButton.on(Stripe.ElementEvent.Ready, () => {
                this.onPaymentRequestButtonReadyEvent(paymentRequestButton, paymentRequestInstance);
            });
            if (!this.container) {
                throw new Error('Container not set');
            }
            paymentRequestButton.mount(this.container.nativeElement);
        }
        this.onAvailabilityUpdate(!!this.paymentRequestButtonElementInstance);
        this.readyCheck();
        this.detectChanges();
    }

    //////////////////////////////////////////
    /////
    /////   Payment reuqest button element instance
    /////
    //////////////////////////////////////////

    /**
     * On payment request button element instance ready event handler
     * @param {Stripe.IPaymentRequestButtonElementInstance}     paymentRequstButton
     * @param { Stripe.IPaymentRequestInstance}                 paymentRequest
     */
    private onPaymentRequestButtonReadyEvent(
        paymentRequstButton: Stripe.IPaymentRequestButtonElementInstance,
        paymentRequest: Stripe.IPaymentRequestInstance
    ): void {
        paymentRequstButton.on(Stripe.ElementEvent.Click, (event: Stripe.IElementInstaceClickEvent) =>
            this.onPaymentRequestButtonClickEvent(event)
        );
        paymentRequest.on(Stripe.PaymentRequestEvent.paymentmethod, (event: Stripe.IPaymentRequestResponse) =>
            this.onPaymentRequestPaymentMethodEvent(event, paymentRequstButton)
        );
    }

    /**
     * On payment request button element instance click event handler
     * @param {Stripe.IElementInstaceClickEvent} event
     */
    private onPaymentRequestButtonClickEvent(event: Stripe.IElementInstaceClickEvent): void {
        this.totalsUpdatingClickStatus.reset();
        const checkoutIsValid = this.checkoutFormComponent.isCheckoutValid;
        if (
            !checkoutIsValid ||
            this.totalsUpdating ||
            this.createPaymentRequestButtonStatus.busy ||
            this.checkoutBusy ||
            this.authenticationStatus.busy
        ) {
            event.preventDefault();
            // if checkout is busy we do nothing
            if (!this.checkoutBusy && !this.authenticationStatus.busy) {
                if (!checkoutIsValid) {
                    this.checkoutFormComponent.validateCheckout();
                } else {
                    this.totalsUpdatingClickStatus.error = 'Your order details are being updated. Please try again in a moment';
                }
            }
        }
    }

    /**
     * Returns payment request element instance create options
     * @param {Stripe.IPaymentRequestInstance} paymentRequest
     * @returns {Stripe.ICreatePaymentRequestButtonOptions}
     */
    private getPaymentRequestButtonCreateOptions(
        paymentRequest: Stripe.IPaymentRequestInstance
    ): Stripe.ICreatePaymentRequestButtonOptions {
        const options: Stripe.ICreatePaymentRequestButtonOptions = { paymentRequest };
        const config = this.getConfig();
        if (config?.buttonConfig) {
            options.style = { paymentRequestButton: config.buttonConfig };
        }
        return options;
    }

    //////////////////////////////////////////
    /////
    /////   Payment reuqest instance
    /////
    //////////////////////////////////////////

    /**
     * On payment request instance payment event handler
     * @param {Stripe.IPaymentRequestResponse} event
     * @param {Stripe.IPaymentRequestButtonElementInstance} paymentRequestButton
     */
    private onPaymentRequestPaymentMethodEvent(
        event: Stripe.IPaymentRequestResponse,
        paymentRequestButton: Stripe.IPaymentRequestButtonElementInstance
    ): void {
        if (!this.authenticationStatus.busy) {
            const submissionData = this.checkoutFormComponent.getSubmissionData();
            submissionData.paymentMethod = this.getSubmissionPaymentData(event);
            this.placeOrder(submissionData, event);
        }
    }

    /**
     * Modify payment request options received from BE
     * @param {Stripe.IPaymentRequestOptions} paymentRequestOptions
     * @returns {Stripe.IPaymentRequestOptions | undefined}
     */
    protected getPaymentRequestOptions(paymentRequestOptions: Stripe.IPaymentRequestOptions): Stripe.IPaymentRequestOptions | undefined {
        if (paymentRequestOptions) {
            const disabledWallets = this.getDisabledWallets(paymentRequestOptions.disableWallets);
            if (disabledWallets.length !== 4) {
                paymentRequestOptions.requestPayerEmail = false;
                paymentRequestOptions.requestPayerName = false;
                paymentRequestOptions.requestPayerPhone = false;
                paymentRequestOptions.requestShipping = false;
                if (paymentRequestOptions.shippingOptions) {
                    delete paymentRequestOptions.shippingOptions;
                }
                paymentRequestOptions.disableWallets = disabledWallets;
                return paymentRequestOptions;
            }
        }
        return undefined;
    }

    /**
     * Returns disabled wallets, disabled by flag in component, not BE config
     * @param {Stripe.IPaymentRequestWallet[]} disabledWallets
     * @returns {Stripe.IPaymentRequestWallet[]}
     */
    protected getDisabledWallets(disabledWallets?: Stripe.IPaymentRequestWallet[]): Stripe.IPaymentRequestWallet[] {
        disabledWallets = disabledWallets || [];
        if (!this.useBrowserCard) {
            disabledWallets.push('browserCard');
        }
        if (!this.useGooglePay) {
            disabledWallets.push('googlePay');
        }
        if (!this.useLink) {
            disabledWallets.push('link');
        }
        if (!this.useApplePay || !this.getConfig()?.isApplePayEnabled) {
            disabledWallets.push('applePay');
        }
        return uniq(disabledWallets);
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns checkout submission payment data
     * @param {string} token
     * @returns {Stripe.ICardSubmissionData}
     */
    private getSubmissionPaymentData(paymentRequestResult: Stripe.IPaymentRequestResponse): Stripe.IPaymentRequestButtonSubmissionData {
        return {
            method: this.methodCode,
            additional_data: {
                result: paymentRequestResult,
                location: 'checkout'
            }
        };
    }

    /**
     * Returns if totals are updating
     * @returns {boolean}
     */
    private get totalsUpdating(): boolean {
        return this.basketTotalsUpdatingService.totalsUpdating;
    }

    /**
     * Sends submit request and process authentication
     * @param {Checkout.ISubmissionData}        submissionData
     * @param {Stripe.IPaymentRequestResponse}  paymentRequestResponse
     */
    private placeOrder(submissionData: Checkout.ISubmissionData, paymentRequestResponse: Stripe.IPaymentRequestResponse): void {
        const status = this.checkoutFormComponent.status;

        const fail = (error?: string) => {
            if (error) {
                status.error = error;
            }
            this.authenticationStatus.reset();
            paymentRequestResponse.complete(Stripe.PaymentRequestCompleteStatus.fail);
        };
        this.checkoutFormComponent.submitWithData(
            submissionData,
            () => {
                paymentRequestResponse.complete(Stripe.PaymentRequestCompleteStatus.success);
                this.authenticationStatus.reset();
            },
            () => {
                fail();
            }
        );

        const stripe = this.stripe;
        if (stripe) {
            this.stripeService.checkAuthentication(
                stripe,
                this.authenticationStatus,
                status,
                () => {
                    this.placeOrder(submissionData, paymentRequestResponse);
                },
                e => {
                    fail(e || Stripe.AUTH_ERROR_MESSAGE);
                }
            );
        }
    }

    //////////////////////////////////////////
    /////
    /////   Checkout data update
    /////
    //////////////////////////////////////////

    /**
     * Checkout submission is in progress
     * @returns {boolean}
     */
    get checkoutBusy(): boolean {
        return this.checkoutFormComponent.status.busy;
    }

    /**
     * On checkout data update
     */
    private onCheckoutDataUpdate(): void {
        if (this.isUpdateRequired()) {
            this.createPaymentRequestButton();
        }
    }

    /**
     * Checks if we have to create new payment request button because of checkout data change
     * @returns {boolean}
     */
    private isUpdateRequired(): boolean {
        if (this.checkoutFormComponent.isFree) {
            return false;
        }
        return JSON.stringify(this.getUpdateRequiredData()) !== this.lastUpdateRequiredData;
    }

    /**
     * Returns update required data
     * @returns {Stripe.IPaymentRequestUpdateRequiredData}
     */
    private getUpdateRequiredData(): Stripe.IPaymentRequestUpdateRequiredData {
        return {
            billingCountry: this.checkoutFormComponent.billingAddress?.country_id,
            total: this.app.customer.basket.data?.totals?.grand_total,
            items: this.app.customer.basket.items.map(basketItem => {
                return {
                    amount: basketItem.rowPrice(),
                    label: basketItem.product?.data?.name,
                    id: basketItem.data?.id
                };
            })
        };
    }
}
